if (!customElements.get('video-player')) {
  /**
   * Custom element for a video player with custom controls.
   * @extends {HTMLElement}
   */
  class VideoPlayer extends HTMLElement {
    /** @type {HTMLVideoElement} */
    video;

    /** @type {HTMLElement} */
    videoControlsContainer;

    /** @type {HTMLButtonElement | null} */
    playpause;

    /** @type {HTMLButtonElement | null} */
    mute;

    /** @type {HTMLButtonElement | null} */
    fullscreen;

    constructor() {
      super();
      // Bind event handlers for safe removal in disconnectedCallback
      this._onPlay = this._onPlay.bind(this);
      this._onPause = this._onPause.bind(this);
      this._onPlayClick = this._onPlayClick.bind(this);
      this._onMuteClick = this._onMuteClick.bind(this);
      this._onFullscreenClick = this.handleFullscreen.bind(this);
      this._onFullscreenChange = this._onFullscreenChange.bind(this);
    }

    connectedCallback() {
      this.video = this.querySelector('video');
      const controlsId = this.dataset.controlsId;
      this.videoControlsContainer = controlsId ? document.getElementById(controlsId) : null;

      if (!this.video) return;

      if (this.videoControlsContainer) {
        this.playpause = this.videoControlsContainer.querySelector('[data-control="playpause"], #playpause');
        this.mute = this.videoControlsContainer.querySelector('[data-control="mute"], #mute');
        this.fullscreen = this.videoControlsContainer.querySelector('[data-control="fs"], #fs');
        this.setVideoControlsEventHandlers();
      }

      if (this.dataset.deferVideo === 'true') {
        this.deferVideoPlayer();
      }

      // Explicitly set native controls from data attribute — never rely on implicit state
      this.video.controls = this.dataset.nativeControls === 'true';
    }

    disconnectedCallback() {
      if (this.video) {
        this.video.removeEventListener('play', this._onPlay);
        this.video.removeEventListener('pause', this._onPause);
      }
      this.playpause?.removeEventListener('click', this._onPlayClick);
      this.mute?.removeEventListener('click', this._onMuteClick);
      this.fullscreen?.removeEventListener('click', this._onFullscreenClick);
      document.removeEventListener('fullscreenchange', this._onFullscreenChange);
    }

    /**
     * Updates the state of the video player buttons.
     * @param {string} type - The type of button to update ('playpause', 'mute', etc.).
     */
    changeButtonState(type) {
      if (type === 'playpause') {
        const state = this.video.paused || this.video.ended ? 'pause' : 'play';
        this.playpause?.setAttribute('data-state', state);
        this.playpause?.setAttribute('aria-label', state === 'pause' ? 'Play video' : 'Pause video');
      } else if (type === 'mute') {
        this.mute?.setAttribute('data-state', this.video.muted ? 'mute' : 'unmute');
        this.mute?.setAttribute('aria-label', this.video.muted ? 'Unmute video' : 'Mute video');
      }
    }

    /**
     * Handles toggling fullscreen mode.
     */
    handleFullscreen() {
      if (document.fullscreenElement !== null) {
        document
          .exitFullscreen()
          .then(() => {
            this.setFullscreenData(false);
            this.changeButtonState('fullscreen');
          })
          .catch((err) => console.error('Failed to exit fullscreen:', err));
      } else {
        const options = { navigationUI: 'show' };
        // @ts-ignore
        const requestFullscreen =
          this.requestFullscreen ||
          this.webkitRequestFullscreen ||
          this.msRequestFullscreen ||
          this.mozRequestFullscreen;

        if (requestFullscreen) {
          requestFullscreen
            .call(this, options)
            .then(() => {
              this.setFullscreenData(true);
              this.changeButtonState('fullscreen');
            })
            .catch((err) => console.error('Failed to enter fullscreen:', err));
        } else {
          console.error('Fullscreen API is not supported by this browser.');
        }
      }
    }

    /**
     * Updates the fullscreen data attribute and button state.
     * @param {boolean} state - True if in fullscreen mode, false otherwise.
     */
    setFullscreenData(state) {
      this.setAttribute('data-fullscreen', !!state);
      this.fullscreen?.setAttribute('data-state', state ? 'cancel-fullscreen' : 'go-fullscreen');
      this.fullscreen?.setAttribute('aria-label', state ? 'Exit full screen' : 'View video full screen');
    }

    /**
     * Defers loading of the video until it is in the viewport.
     */
    deferVideoPlayer() {
      // Optimization: Removed redundant elementInViewport check (layout thrashing).
      // IntersectionObserver will trigger immediately if already visible.
      if (!window.IntersectionObserver) {
        this.loadVideo();

        return;
      }

      const observer = new IntersectionObserver(
        (entries, obs) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.loadVideo();
              obs.unobserve(entry.target);
            }
          });
        },
        { rootMargin: '0px 0px 10% 0px' }
      );
      observer.observe(this);
    }

    /**
     * Loads the video sources.
     */
    loadVideo() {
      const sources = this.querySelectorAll('source');
      if (sources.length === 0) return;

      let hasChanges = false;
      sources.forEach((element) => {
        if (element.dataset.src) {
          element.src = element.dataset.src;
          // Optimization: Removed individual element.dataset.src deletion if preferred,
          // but keeping code minimal.
          hasChanges = true;
        }
      });

      // Optimization: Call load() once after updating all sources
      if (hasChanges) {
        this.video.load();
      }
    }

    /**
     * Sets up event handlers for the video controls.
     */
    setVideoControlsEventHandlers() {
      if (this.video) {
        this.video.addEventListener('play', this._onPlay);
        this.video.addEventListener('pause', this._onPause);
      }

      this.playpause?.addEventListener('click', this._onPlayClick);
      this.mute?.addEventListener('click', this._onMuteClick);
      this.fullscreen?.addEventListener('click', this._onFullscreenClick);

      document.addEventListener('fullscreenchange', this._onFullscreenChange);
    }

    // --- Event Handlers ---

    _onPlay() {
      this.changeButtonState('playpause');
    }

    _onPause() {
      this.changeButtonState('playpause');
    }

    _onPlayClick() {
      if (this.video.paused || this.video.ended) {
        this.video.play();
      } else {
        try {
          this.video.pause();
        } catch (e) {
          console.error('Error pausing video:', e);
        }
      }
    }

    _onMuteClick() {
      this.video.muted = !this.video.muted;
      this.changeButtonState('mute');
    }

    _onFullscreenChange() {
      // Optimization: Verify the fullscreen element is this component
      this.setFullscreenData(document.fullscreenElement === this);
    }
  }

  customElements.define('video-player', VideoPlayer);
}